import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

const SIDE = "https://images.unsplash.com/photo-1749497636414-ce2aae2df56c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBwZXJmdW1lJTIwYm90dGxlJTIwZ29sZCUyMGNoYW1wYWduZXxlbnwwfHx8fDE3ODg4MTM4OTV8MA&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0B0B0C] grid lg:grid-cols-2 pt-20 lg:pt-0">
      <div className="hidden lg:block relative">
        <img src={SIDE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/40 to-transparent" />
        <div className="absolute bottom-16 left-12 right-12">
          <p className="font-serif-lux text-4xl gold-text italic leading-tight">Your signature awaits.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="font-serif-lux text-3xl gold-text block mb-2">QUEEN MJ MAISON BEAUTE</Link>
          <p className="eyebrow mb-8">{subtitle}</p>
          <h1 className="font-serif-lux text-4xl font-light text-[#FAFAF7] mb-8">{title}</h1>
          {children}
          <button data-testid="google-login-button" onClick={googleLogin}
            className="w-full mt-4 gold-border rounded-full py-3 text-sm text-[#FAFAF7] hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
            <span className="font-medium">G</span> Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome Back" subtitle="SIGN IN">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-[#141416] gold-border rounded-lg px-4 py-3 text-sm text-[#FAFAF7] outline-none focus:border-[#D4AF37]" />
        <input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-[#141416] gold-border rounded-lg px-4 py-3 text-sm text-[#FAFAF7] outline-none focus:border-[#D4AF37]" />
        <button data-testid="login-submit" disabled={busy} className="btn-gold w-full py-3.5 rounded-full text-sm disabled:opacity-60">{busy ? "Signing in…" : "Sign In"}</button>
      </form>
      <p className="text-sm text-[#FAFAF7]/50 mt-6 text-center">New to Queen MJ Maison Beaute? <Link to="/register" className="text-[#D4AF37] link-underline">Create account</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Welcome to Queen MJ Maison Beaute");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Join the Maison" subtitle="CREATE ACCOUNT">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="register-name" required placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full bg-[#141416] gold-border rounded-lg px-4 py-3 text-sm text-[#FAFAF7] outline-none focus:border-[#D4AF37]" />
        <input data-testid="register-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-[#141416] gold-border rounded-lg px-4 py-3 text-sm text-[#FAFAF7] outline-none focus:border-[#D4AF37]" />
        <input data-testid="register-password" type="password" required minLength={6} placeholder="Password (min 6 chars)" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-[#141416] gold-border rounded-lg px-4 py-3 text-sm text-[#FAFAF7] outline-none focus:border-[#D4AF37]" />
        <button data-testid="register-submit" disabled={busy} className="btn-gold w-full py-3.5 rounded-full text-sm disabled:opacity-60">{busy ? "Creating…" : "Create Account"}</button>
      </form>
      <p className="text-sm text-[#FAFAF7]/50 mt-6 text-center">Already a member? <Link to="/login" className="text-[#D4AF37] link-underline">Sign in</Link></p>
    </AuthShell>
  );
}
